import { useMemo, useState } from 'react'

import { useFunnelForms } from '../../hooks/use-feature-flags'
import {
  METRIC_LABELS,
  TRAFFIC_WINDOW_DAYS,
  estimateRuntime,
  formatNumber,
  funnelStatsFromForms
} from '../../helper/experiments'
import { Notice } from './ExperimentBadges'
import { Explain, HELP } from './Help'

const inputClass =
  'block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
const labelClass = 'block text-xs font-medium text-gray-700 mb-1'
const hintClass = 'mt-1 text-xs text-gray-500'

const round1 = (value) => Math.round(value * 10) / 10

// Planungshilfe, nichts davon wird im Flag gespeichert. Leere Eingaben (null)
// zeigen den Vorschlag aus den Funnel-Forms, sobald jemand tippt, gilt der
// eigene Wert.
const RuntimeEstimator = ({ variants, decision, surface }) => {
  const isFunnel = surface === 'funnel'
  const {
    data: forms,
    isLoading,
    isError
  } = useFunnelForms({
    enabled: isFunnel
  })
  const stats = useMemo(
    () => (isFunnel && forms ? funnelStatsFromForms(forms) : null),
    [isFunnel, forms]
  )

  const [baseline, setBaseline] = useState(null)
  const [effect, setEffect] = useState('50')
  const [traffic, setTraffic] = useState(null)

  const primary = decision?.primary
  const suggestedTraffic = stats?.starts ? round1(stats.perDay) : null
  const suggestedRate = stats?.rates?.[primary]
  const suggestedBaseline =
    suggestedRate !== null && suggestedRate !== undefined
      ? round1(suggestedRate * 100)
      : null

  const trafficValue =
    traffic ?? (suggestedTraffic !== null ? String(suggestedTraffic) : '')
  const baselineValue =
    baseline ?? (suggestedBaseline !== null ? String(suggestedBaseline) : '')

  const estimate = estimateRuntime({
    baseline: Number(baselineValue) / 100,
    relativeEffect: Number(effect) / 100,
    dailyTraffic: Number(trafficValue),
    variants,
    decision
  })

  const trafficHint = !isFunnel
    ? 'Für diese Surface bitte manuell eintragen.'
    : isLoading
      ? 'Funnel-Starts werden geladen ...'
      : isError
        ? 'Funnel-Starts konnten nicht geladen werden, bitte manuell eintragen.'
        : stats
          ? `Vorbefüllt aus ${formatNumber(stats.starts)} Funnel-Starts der letzten ${TRAFFIC_WINDOW_DAYS} Tage.`
          : null

  const baselineHint =
    suggestedBaseline !== null
      ? `Vorschlag aus den Funnel-Forms der letzten ${TRAFFIC_WINDOW_DAYS} Tage für ${METRIC_LABELS[primary] || primary}.`
      : `Rate der primären Metrik (${METRIC_LABELS[primary] || primary || '-'}) bei Control.`

  return (
    <div className='rounded-md bg-gray-50 p-4 ring-1 ring-inset ring-gray-200'>
      <div className='flex items-center gap-2'>
        <h4 className='text-sm font-semibold text-gray-900'>
          Laufzeitschätzer
        </h4>
        <span className='inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20'>
          Schätzung
        </span>
      </div>
      <p className={hintClass}>
        Reicht der Traffic für einen Test? Die Zahlen sind nur zur Planung und
        werden nicht gespeichert.
      </p>
      <Explain className='mt-1'>{HELP.estimator}</Explain>

      <div className='mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3'>
        <div>
          <label htmlFor='estimator-baseline' className={labelClass}>
            Baseline-Conversionrate (%)
          </label>
          <input
            id='estimator-baseline'
            type='number'
            min='0'
            max='100'
            step='any'
            className={inputClass}
            value={baselineValue}
            onChange={(e) => setBaseline(e.target.value)}
            placeholder='z. B. 5'
          />
          <p className={hintClass}>{baselineHint}</p>
        </div>
        <div>
          <label htmlFor='estimator-effect' className={labelClass}>
            Minimal relevanter Effekt (relativ, %)
          </label>
          <input
            id='estimator-effect'
            type='number'
            step='any'
            className={inputClass}
            value={effect}
            onChange={(e) => setEffect(e.target.value)}
            placeholder='z. B. 50'
          />
          <p className={hintClass}>
            50 heißt, aus 5 % sollen mindestens 7,5 % werden.
          </p>
        </div>
        <div>
          <label htmlFor='estimator-traffic' className={labelClass}>
            Täglicher Traffic (Units pro Tag)
          </label>
          <input
            id='estimator-traffic'
            type='number'
            min='0'
            step='any'
            className={inputClass}
            value={trafficValue}
            onChange={(e) => setTraffic(e.target.value)}
            placeholder='z. B. 40'
          />
          {trafficHint && <p className={hintClass}>{trafficHint}</p>}
        </div>
      </div>

      <div className='mt-4'>
        {!estimate ? (
          <p className='text-sm text-gray-500'>
            Trag Baseline und Effekt ein. Die Zielrate muss zwischen 0 und 100 %
            liegen.
          </p>
        ) : (
          <dl className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
            <div>
              <dt className='text-xs font-medium text-gray-500'>
                Nutzer pro Variante
              </dt>
              <dd className='mt-1 text-lg font-semibold text-gray-900'>
                {formatNumber(estimate.perVariant)}
              </dd>
            </div>
            <div>
              <dt className='text-xs font-medium text-gray-500'>
                Units insgesamt
              </dt>
              <dd className='mt-1 text-lg font-semibold text-gray-900'>
                {formatNumber(estimate.totalUnits)}
              </dd>
            </div>
            <div>
              <dt className='text-xs font-medium text-gray-500'>
                Geschätzte Dauer
              </dt>
              <dd className='mt-1 text-lg font-semibold text-gray-900'>
                {estimate.days === null
                  ? 'Traffic fehlt'
                  : `ca. ${formatNumber(estimate.days)} Tage`}
              </dd>
            </div>
          </dl>
        )}

        {estimate?.days !== null &&
          estimate?.days !== undefined &&
          estimate.days > estimate.daysForSample && (
            <p className={hintClass}>
              Rechnerisch reichen {formatNumber(estimate.daysForSample)} Tage,
              die Mindestlaufzeit (min_days) ist aber länger.
            </p>
          )}

        {estimate?.exceedsMaxDays && (
          <Notice tone='warning' className='mt-3'>
            Das liegt über max_days ({formatNumber(decision?.max_days)} Tage).
            Der Test endet dann voraussichtlich ohne Gewinner mit Control. Mehr
            Traffic, ein größerer Effekt oder eine längere Laufzeit helfen.
          </Notice>
        )}
      </div>

      <p className='mt-4 text-xs text-gray-500'>
        Schätzung für Control gegen eine Variante mit dem Zwei-Proportionen-Test
        (alpha 0,05 zweiseitig, Power 0,8). Die kleinste Variante bestimmt die
        Dauer, min_days und min_exposures_per_variant gelten zusätzlich. Der
        Evaluator entscheidet über P(best), die Zahlen dienen nur zur Planung
        und werden nicht gespeichert.
      </p>
    </div>
  )
}

export default RuntimeEstimator
